import { CalendarPlus, CalendarSearch, CircleDot, Droplets, ExternalLink, ListTodo, Wrench } from 'lucide-react';
import type { ComponentType } from 'react';
import { cn } from 'shared-ui';

import type { NetworkTableAreaRow, NetworkTableDmaRow, NetworkTablePoiRow } from '@/api/networkTableApi';
import { What3WordsLogo } from '@/components/map/components/What3WordsLogo';
import { StatusPill } from '@/components/shared/StatusPill';
import { getPoiStatusPillPresentation } from '@/components/shared/statusPillPresentation';
import {
    formatPipeType,
    formatRepaired,
    formatSearchDate,
    getLeakageRateDisplay,
    formatLeakageRateMlD,
    what3WordsUrl,
} from '@/components/map/utils/poiFeatureProperties';
import { formatIsoDateTime } from '@/utils/dateUtils';

import { mapTablePoiToFeatureProperties } from './mapTablePoiToFeatureProperties';

type FieldIcon = ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean }>;

/**
 * The POI's facts, revealed by expanding its row: a read-only information grid, not a form. It
 * deliberately does not imitate the POI details panel's control-shaped fields — this is a summary
 * you scan, and anything that looks like an input invites a click that does nothing.
 *
 * Worklist and Stop are deliberately absent: the row's collapsed identity line stays on screen
 * above this grid while it is open, so repeating them here would say nothing twice.
 *
 * Every value comes from the row DTO already in hand, so expanding costs no request.
 */
export function PoiSummaryGrid({
    poi,
    area,
    dma,
    isMaximised,
    isMobile = false,
}: {
    poi: NetworkTablePoiRow;
    area: NetworkTableAreaRow;
    dma: NetworkTableDmaRow;
    /** Drives the widest grid step: only the maximised table has room for four columns. */
    isMaximised: boolean;
    /** A phone keeps the facts in one vertical column with a minimal inset. */
    isMobile?: boolean;
}) {
    // One documented mapping feeds the summary, Copy and Show Details alike — so the leakage
    // measured/assumed decision here can never disagree with the clipboard or the panel.
    const properties = mapTablePoiToFeatureProperties(poi, { area, dma });
    const leakage = getLeakageRateDisplay(properties);
    const pipeType = formatPipeType(poi.pipeType);
    const repaired = formatRepaired(poi.repaired);

    // `created` is a timestamp ("yyyy-MM-ddTHH:mm:ss"); the date-only fields are "yyyy-MM-dd".
    // They need different formatters — see formatIsoDateTime.
    const created = formatIsoDateTime(poi.created);
    const searched = formatSearchDate(poi.searchDate);

    return (
        <dl
            className={cn(
                // Columns step with the space actually available: the table is half-width in split
                // mode, so the four-column step is offered only when maximised. No fixed widths, so
                // the grid keeps adapting between those steps. Rows sit 16px apart per the design.
                // A phone stays a single readable column at a minimal inset — the multi-column
                // steps and the deep indent are desktop affordances (gated in JS, not with
                // viewport variants, per this bundle's emission-order quirk).
                'grid grid-cols-1 gap-x-6 gap-y-4 pt-3',
                isMobile ? 'pl-2' : 'pl-8 sm:grid-cols-2',
                !isMobile && isMaximised && 'xl:grid-cols-4',
            )}
        >
            {/* Field order is the designer's, verbatim; Assessment Result leads the grid. */}
            <PoiField icon={ListTodo} label="Assessment Result">
                {/* The one status mapping, shared with the map — and the table's ONLY status
                    display: the collapsed row carries identity alone, per the design. */}
                <StatusPill presentation={getPoiStatusPillPresentation(poi.status)} />
            </PoiField>

            <PoiField icon={What3WordsLogo} label="POI What3words" qualifier="Original">
                <What3WordsValue value={poi.whatThreeWords} linkLabel="Open original What3Words location" />
            </PoiField>

            {/* Date Created is poi.created — NOT surveyed_date, which the POI details panel labels
                this way. That panel's mapping is a known defect and is not copied here. */}
            <PoiField icon={CalendarPlus} label="Date Created">
                {created ?? <UnavailableValue />}
            </PoiField>

            <PoiField icon={Wrench} label="Repaired?">
                {repaired ?? <UnavailableValue />}
            </PoiField>

            <PoiField icon={What3WordsLogo} label="Leak What3words" qualifier="Actual">
                <What3WordsValue value={poi.leakWhatThreeWords} linkLabel="Open actual leak What3Words location" />
            </PoiField>

            <PoiField icon={CalendarSearch} label="Date of Search">
                {searched ?? <UnavailableValue />}
            </PoiField>

            <PoiField icon={CircleDot} label="Pipe Type">
                {pipeType ?? <UnavailableValue />}
            </PoiField>

            {/* Both the unit and the provenance ride the label — "Leakage Rate Ml/D (Assumed)" —
                leaving the value as the bare number. The qualifier is never dropped: whether a rate
                was measured or inferred changes what the number means, so it is not decoration. */}
            <PoiField icon={Droplets} label="Leakage Rate Ml/D" qualifier={leakage.provenance}>
                <span className="tabular-nums">{formatLeakageRateMlD(leakage.value)}</span>
            </PoiField>
        </dl>
    );
}

/**
 * One label/value block. `dt`/`dd` rather than styled spans, so the pairing survives for a screen
 * reader reading the grid linearly — the icon is decorative and carries no meaning on its own.
 */
function PoiField({
    icon: Icon,
    label,
    qualifier,
    children,
}: {
    icon: FieldIcon;
    label: string;
    /** Distinguishes two fields sharing a label (the Original/Actual What3Words pair). */
    qualifier?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex min-w-0 items-start gap-3">
            <span className="bg-background border-border flex size-9 shrink-0 items-center justify-center rounded-md border">
                <Icon className="text-origin-blue size-4" strokeWidth={2} aria-hidden={true} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
                {/*
                 * `--foreground`, NOT `--primary`. The design calls this colour "primary" meaning the
                 * primary TEXT colour (near-black / near-white); shared-ui's `--primary` means the
                 * primary BRAND colour and is Origin Blue in both themes. Same word, different thing
                 * — `text-primary` here renders blue labels.
                 */}
                <dt className="text-foreground text-xs leading-4 font-medium">
                    {label}
                    {/* Muted and regular-weight, as the POI details panel draws its "(Original)" /
                        "(Actual)" / "(Assumed)" meta — the qualifier annotates the label, it is
                        not part of it. */}
                    {qualifier != null && <span className="text-muted-foreground font-normal"> ({qualifier})</span>}
                </dt>
                {/* Values are Poppins 400 / text-sm / leading-5 / tracking-normal per the design —
                    the family, spacing and tracking all arrive by inheritance, so only size, leading
                    and weight are declared. The what3words links inherit this too, adding just their
                    underline. break-words, not truncate: a long what3words must fold rather than be
                    silently clipped, and must never push the row into horizontal overflow. */}
                <dd className="text-muted-foreground min-w-0 text-sm leading-5 font-normal break-words">{children}</dd>
            </div>
        </div>
    );
}

/**
 * The standard unavailable value: an em dash carries it visually, real words carry it to a screen
 * reader, which would otherwise hear the dash as nothing at all.
 *
 * Exported because the table's own cells need it too (the Unassigned DMA's Reference), and a second
 * bare "—" elsewhere would be the exact accessibility gap this exists to close.
 */
export function UnavailableValue() {
    return (
        <span className="text-muted-foreground font-normal">
            <span aria-hidden="true">—</span>
            <span className="sr-only">Not available</span>
        </span>
    );
}

/**
 * A what3words address as a link to the w3w map. With no value there is no link — an external-link
 * affordance that navigates nowhere is worse than an honest em dash.
 *
 * The address is rendered bare, without the "///" brand prefix the map's hover cards use: this
 * field's icon container already carries the what3words mark, so the slashes only repeated it.
 */
function What3WordsValue({ value, linkLabel }: { value: string | null; linkLabel: string }) {
    if (value === null || value === '') return <UnavailableValue />;

    return (
        <a
            href={what3WordsUrl(value)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={linkLabel}
            className="text-primary inline-flex min-w-0 items-center gap-1.5 underline underline-offset-2"
        >
            <span className="min-w-0 break-words">{value}</span>
            <ExternalLink className="size-3.5 shrink-0" strokeWidth={2} aria-hidden={true} />
        </a>
    );
}
